import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import type { Notification } from '@/types/notifications';
import { notificationDeliveryService } from '@/services/NotificationDeliveryService';
import { focusModeNotificationService } from '@/services/FocusModeNotificationService';
import { NotificationBannerStack } from '@/components/notifications/NotificationBanner';
import { useUser } from '@clerk/clerk-react';
import { useOrganizationContext } from '@/contexts/OrganizationContext';
import { useFocusMode } from '@/hooks/useFocusMode';

/**
 * Play notification sound
 */
function playNotificationSound(soundId: string, priority?: string): void {
  try {
    const soundMap: Record<string, string> = {
      'message': '/sounds/message.mp3',
      'alert': '/sounds/alert.mp3',
      'reminder': '/sounds/reminder.mp3',
      'alarm': '/sounds/alarm.mp3',
      'system': '/sounds/system.mp3',
    };

    const soundUrl = soundMap[soundId] || soundMap['system'];
    const audio = new Audio(soundUrl);

    if (priority === 'urgent') {
      audio.volume = 1.0;
    } else if (priority === 'high') {
      audio.volume = 0.8;
    } else {
      audio.volume = 0.6;
    }

    audio.play().catch((error) => {
      console.warn('[NotificationManager] Failed to play sound:', error);
    });
  } catch (error) {
    console.warn('[NotificationManager] Sound playback error:', error);
  }
}

/**
 * Simplified NotificationManager
 * 
 * Provides basic notification banner management without real-time delivery.
 * This version avoids circular dependencies by not importing complex components.
 */

interface NotificationManagerContextValue {
  // Banner display
  showBanner: (notification: Notification) => void;
  hideBanner: (notificationId: string) => void;
  activeBanners: Notification[];
  
  // Connection status (always true for simplified version)
  isConnected: boolean;
  connectionError: Error | null;
}

const NotificationManagerContext = createContext<NotificationManagerContextValue | undefined>(
  undefined
);

interface NotificationManagerProviderProps {
  children: React.ReactNode;
}

/**
 * NotificationManagerProvider with Real-Time Delivery
 * 
 * Manages notification banners with real-time Supabase subscriptions.
 * Safe initialization prevents circular dependency issues.
 */
export function NotificationManagerProvider({ children }: NotificationManagerProviderProps) {
  const { user } = useUser();
  const { currentOrganizationId } = useOrganizationContext();
  const { activeFocusMode } = useFocusMode(); // Get active focus mode
  const [activeBanners, setActiveBanners] = useState<Notification[]>([]);
  const [isConnected, setIsConnected] = useState(false);

  /**
   * Show banner for notification
   */
  const showBanner = useCallback((notification: Notification) => {
    setActiveBanners((prev) => {
      // Don't show duplicate banners
      if (prev.some((n) => n.id === notification.id)) {
        return prev;
      }
      
      // Limit to 3 banners max
      const newBanners = [notification, ...prev].slice(0, 3);
      return newBanners;
    });

    // Auto-dismiss after duration
    const duration = notification.auto_dismiss_after
      ? notification.auto_dismiss_after * 1000
      : 5000;

    setTimeout(() => {
      hideBanner(notification.id);
    }, duration);
  }, []);

  /**
   * Hide banner
   */
  const hideBanner = useCallback((notificationId: string) => {
    setActiveBanners((prev) => prev.filter((n) => n.id !== notificationId));
  }, []);

  /**
   * Sync active focus mode to the filtering service
   */
  useEffect(() => {
    focusModeNotificationService.setActiveFocusMode(activeFocusMode || null);
  }, [activeFocusMode]);

  /**
   * Initialize real-time delivery when user and org are ready
   */
  useEffect(() => {
    if (!user?.id || !currentOrganizationId) {
      setIsConnected(false);
      return;
    }

    const initialize = async () => {
      try {
        await notificationDeliveryService.initialize(user.id, currentOrganizationId);
        
        // Load channels for focus mode filtering
        await focusModeNotificationService.loadChannels(currentOrganizationId);
        
        setIsConnected(true);
        console.log('[NotificationManager] ✅ Real-time delivery initialized');
      } catch (error) {
        console.error('[NotificationManager] ❌ Failed to initialize:', error);
        setIsConnected(false);
      }
    };

    initialize();

    return () => {
      notificationDeliveryService.cleanup();
      focusModeNotificationService.reset();
      setIsConnected(false);
    };
  }, [user?.id, currentOrganizationId]);

  /**
   * Subscribe to new notifications
   */
  useEffect(() => {
    if (!isConnected) return;

    const unsubscribe = notificationDeliveryService.onNotification((notification) => {
      console.log('[NotificationManager] 📬 New notification:', notification);

      // Apply focus mode filtering (NON-BREAKING: only filters if focus mode is active)
      const filterResult = focusModeNotificationService.shouldDeliver(notification);
      
      if (!filterResult.allowed) {
        console.log('[NotificationManager] 🛡️ Blocked by focus mode:', filterResult.reason);
        
        // Log blocked notification for history/analytics
        focusModeNotificationService.logBlockedNotification(notification, filterResult);
        
        // Don't show banner or play sound - notification is filtered
        return;
      }

      // Notification passed focus mode filter - proceed with normal delivery
      console.log('[NotificationManager] ✅ Notification allowed by focus mode');

      // Show banner for unread notifications
      if (!notification.is_read) {
        showBanner(notification);
      }

      // Play sound if specified
      if (notification.sound) {
        playNotificationSound(notification.sound, notification.priority);
      }

      // Browser notification for urgent
      if (notification.priority === 'urgent' && 'Notification' in window) {
        if (Notification.permission === 'granted') {
          new Notification(notification.title, {
            body: notification.body,
            icon: notification.media?.avatar || '/icon-192.png',
            tag: notification.id,
            requireInteraction: notification.requires_interaction,
          });
        }
      }
    });

    return unsubscribe;
  }, [isConnected, showBanner]);

  /**
   * Request browser notification permission on mount
   */
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const value: NotificationManagerContextValue = {
    showBanner,
    hideBanner,
    activeBanners,
    isConnected,
    connectionError: null,
  };

  return (
    <NotificationManagerContext.Provider value={value}>
      {children}
      {/* Notification banner stack */}
      <NotificationBannerStack
        notifications={activeBanners}
        onDismiss={hideBanner}
        onMarkAsRead={async (id) => {
          try {
            await notificationDeliveryService.markAsRead(id);
          } catch (error) {
            console.error('[NotificationManager] Failed to mark as read:', error);
          }
        }}
      />
    </NotificationManagerContext.Provider>
  );
}

/**
 * Hook to access notification manager
 */
export function useNotificationManager() {
  const context = useContext(NotificationManagerContext);
  
  if (!context) {
    throw new Error('useNotificationManager must be used within NotificationManagerProvider');
  }
  
  return context;
}
